import Component from '@glimmer/component';
import type { TOC } from '@ember/component/template-only';
import { concat } from '@ember/helper';
import style from 'ember-style-modifier/modifiers/style';
import { PhCheckCircle, PhXCircle, PhWarning, PhInfo, PhX } from 'ember-phosphor-icons';
import type { ToastIntent } from 'tio-ui/color-tokens';

export interface ToastItem {
  id: string;
  message: string;
  intent: ToastIntent;
  duration: number;
  visible: boolean;
  /** Whether the close button is shown. @defaultValue true */
  allowClosing?: boolean;
  /** Optional action label (renders a clickable button on the toast) */
  actionLabel?: string;
  /** Callback when action is clicked */
  onAction?: () => void;
}

interface SingleToastSignature {
  Args: {
    toast: ToastItem;
    onDismiss: (id: string) => void;
  };
  Element: HTMLDivElement;
}

class SingleToast extends Component<SingleToastSignature> {
  get showCloseButton() {
    return this.args.toast.allowClosing !== false;
  }

  get roleAttr() {
    return this.args.toast.intent === 'error' ? 'alert' : 'status';
  }

  get ariaLive() {
    return this.args.toast.intent === 'error' ? 'assertive' : 'polite';
  }

  get showProgress() {
    return this.args.toast.duration > 0;
  }

  handleDismiss = () => {
    this.args.onDismiss(this.args.toast.id);
  };

  handleAction = () => {
    this.args.toast.onAction?.();
    this.args.onDismiss(this.args.toast.id);
  };

  <template>
    <div
      data-test-toast
      data-test-toast-intent={{@toast.intent}}
      role={{this.roleAttr}}
      aria-live={{this.ariaLive}}
      class="app-toast app-toast--{{@toast.intent}} {{if @toast.visible 'app-toast--visible'}}"
      ...attributes
    >
      <div class="app-toast-body">
        <div class="app-toast-icon" data-test-toast-icon>
          {{#if (eq @toast.intent "success")}}
            <PhCheckCircle @size={{20}} />
          {{else if (eq @toast.intent "error")}}
            <PhXCircle @size={{20}} />
          {{else if (eq @toast.intent "warning")}}
            <PhWarning @size={{20}} />
          {{else}}
            <PhInfo @size={{20}} />
          {{/if}}
        </div>

        <div class="app-toast-content">
          <p data-test-toast-message class="app-toast-message">
            {{@toast.message}}
          </p>
          {{#if @toast.actionLabel}}
            <button
              type="button"
              class="app-toast-action"
              data-test-toast-action
              {{on "click" this.handleAction}}
            >
              {{@toast.actionLabel}}
            </button>
          {{/if}}
        </div>

        {{#if this.showCloseButton}}
          <button
            type="button"
            class="app-toast-close"
            data-test-toast-close
            aria-label="Dismiss"
            {{on "click" this.handleDismiss}}
          >
            <PhX @size={{14}} />
          </button>
        {{/if}}
      </div>

      {{#if (eq @toast.intent "success")}}
        <div
          class="absolute top-2 left-6 pointer-events-none
            {{if @toast.visible 'reimagined-confetti' ''}}"
        >
          <span class="reimagined-confetti-dot reimagined-confetti-1"></span>
          <span class="reimagined-confetti-dot reimagined-confetti-2"></span>
          <span class="reimagined-confetti-dot reimagined-confetti-3"></span>
          <span class="reimagined-confetti-dot reimagined-confetti-4"></span>
          <span class="reimagined-confetti-dot reimagined-confetti-5"></span>
        </div>
      {{/if}}

      {{#if this.showProgress}}
        <div class="app-toast-progress-track">
          <div
            data-test-toast-progress
            class="app-toast-progress-fill {{if @toast.visible 'app-toast-progress-running' ''}}"
            {{style animationDuration=(concat @toast.duration "ms")}}
          ></div>
        </div>
      {{/if}}
    </div>
  </template>
}

interface ToastContainerSignature {
  Args: {
    /** Array of toast items to render (from the toast service) */
    toasts: ToastItem[];
    /** Called when a toast is dismissed */
    onDismiss: (id: string) => void;
  };
  Element: HTMLDivElement;
}

/**
 * Render one toast stack in the app layout, bound to the service’s toasts and dismiss action.
 * Intent sets the shared accent and background variables.
 */
const ToastContainer: TOC<ToastContainerSignature> = <template>
  <div
    data-test-toast-container
    class="fixed bottom-6 right-6 z-[60] flex flex-col-reverse gap-3 pointer-events-none"
    role="region"
    aria-label="Notifications"
    ...attributes
  >
    {{#each @toasts as |toast|}}
      <div class="pointer-events-auto">
        <SingleToast @toast={{toast}} @onDismiss={{@onDismiss}} />
      </div>
    {{/each}}
  </div>
</template>;

export default ToastContainer;

export type { ToastContainerSignature, ToastIntent };
